import React, { useState, useEffect, useContext, useMemo } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, Slider, Switch, Tab, Tabs, TextField, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { PrivateRoutesContext } from "app/PrivateRoutesContext";
import { apiGet, apiPost } from "app/Services/ApiService";
import { apiUrls } from "common/ApiUrls";
import { Utils } from "common/Utils";
import AximDropdown from "components/Dropdown/AximDropdown";

import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import { makeStyles } from '@mui/styles';

import AximGantt from "components/AximGantt";
import ConfirmDialog from "components/Content/Modal/ConfirmDialog";

import moment from "moment";
import LoaderAnimation from "pages/LoaderAnimation";
import BrdDocumentLink from "components/Button/BrdDocumentLink";

const useStyles = makeStyles((theme) => ({
    complete: {
        '& .bar' : {
            stroke: `${theme.palette?.Green?.main} !important`,
            "stroke-width": 1,
        },
        '& .bar-progress' : {
            fill: `${theme.palette?.Green?.main} !important`
        },
    },
    atRisk: {
        '& .bar' : {
            stroke: `${theme.palette?.Yellow?.main} !important`,
            "stroke-width": 1,
        },
        '& .bar-progress' : {
            fill: `${theme.palette?.Yellow?.main} !important`
        },
    },
    overdue: {
        '& .bar' : {
            stroke: `${theme.palette?.Red?.main} !important`,
            "stroke-width": 1,
        },
        '& .bar-progress' : {
            fill: `${theme.palette?.Red?.main} !important`
        },
    },
    onTime: {
        '& .bar-progress' : {
            fill: `${theme.palette?.Grey?.main} !important`
        },
    },
    milestone: {
        '& .milestone' : {
            fill: 'dodgerblue !important'
        },
        '& .bar-label' : {
            fill: 'black !important'
        }
    },
    gantt: {
        '& .today-highlight': {
            fill: `${theme.palette?.primary?.main} !important`,
        },
        '& .gantt-container': {
            "max-height": "calc(100vh - 230px);",
        },
    }
}));

function EditDialog({ doShow, buttonsDisabled, task, completeEdit }) {
    const [name, setName] = useState(null);
    const [start, setStart] = useState(null);
    const [end, setEnd] = useState(null);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (doShow && task) {
            setName(task.name);
            setStart(Utils.convertToDatePicker(task.start));
            setEnd(Utils.convertToDatePicker(task.end));
            setProgress(task.progress);
        }
    },[doShow, task]);

    return (
        <Dialog
            open={doShow}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            maxWidth="sm"
            fullWidth={true}
        >
            <DialogTitle id="alert-dialog-title">{name}</DialogTitle>
            <DialogContent>
                <Box className="container">
                    <Box className="row">
                        <Box className="col-12 mb-2">
                            <TextField
                                style={{width:"100%"}}
                                variant="standard"
                                label="Name"
                                value={name}
                                onChange={e => setName(e.target.value)}
                            />
                        </Box>
                    </Box>
                    <Box className="row">
                        <Box className="col-6 mb-2">
                            <TextField
                                variant="standard"
                                label="Start Date"
                                type="date"
                                value={start}
                                onChange={e => setStart(e.target.value)}
                            />
                        </Box>
                        <Box className="col-6 mb-2">
                            <TextField
                                variant="standard"
                                label="End Date"
                                type="date"
                                value={end}
                                onChange={e => setEnd(e.target.value)}
                            />
                        </Box>
                    </Box>
                    <Box className="row">
                        <Box className="col-12 mb-2">
                            <Typography gutterBottom>Progress</Typography>
                            <Slider 
                                className="mt-4"
                                aria-label="completion"
                                value={progress}
                                onChange={((e, newValue) => setProgress(newValue))}
                                valueLabelDisplay="auto"
                            />
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <LoadingButton
                    onClick={() => completeEdit(true, { id: task.id, name, start, end, progress })}
                    color="primary"
                    variant="outlined"
                    loadingPosition="center"
                    loading={buttonsDisabled}
                >
                    Ok
                </LoadingButton>
                <Button
                    onClick={() => completeEdit(false)}
                    color="primary"
                    variant="outlined"
                    disabled={buttonsDisabled}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    )
}

function ContactAXIM() {
    const { feedback } = useContext(PrivateRoutesContext);
    
    const handleContactAXIM = () => {
        feedback.setType('Configuration Inquiries');
        feedback.setSubject('Update Implementation Timeline');
        feedback.setDoShow(true);
    }

    return (
        <Box className="d-flex mb-2" style={{ alignItems: "center" }}>
            <Typography variant="body2">To collaborate on this Timeline please contact Align Admin</Typography>
            <Button
                className="ms-2"
                variant="outlined"
                onClick={handleContactAXIM}
                color="primary"
            >
                Contact Align Admin
            </Button>
        </Box>
    );
}

export default function BrdTimeline () {
    const { companyId, feedback, userInfo } = useContext(PrivateRoutesContext);

    const [isInitializing, setIsInitializing] = useState(false)

    const [clientEffectiveDate, setClientEffectiveDate] = useState(Utils.convertToDatePicker(moment().add(3, 'months')));
    const [timelineStartDate, setTimelineStartDate] = useState(Utils.convertToDatePicker(moment().add(1, 'months')));
    const [timelineType, setTimelineType] = useState("compliance-only");

    const [isLoading, setIsLoading] = useState(false);

    const [isEditing, setIsEditing] = useState(false);
    const [timeline, setTimeline] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [currentTasks, setCurrentTasks] = useState([]);
    const [updatedTasks, setUpdatedTasks] = useState([]);
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);

    const [doShowConfirm, setDoShowConfirm] = useState(false);
    const [confirmCloseAction, setConfirmCloseAction] = useState(null);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [confirmButtonsDisabled, setConfirmButtonsDiabled] = useState(false);

    const [doShowTaskDialog, setDoShowTaskDialog] = useState(false);
    const [editTask, setEditTask] = useState(null);
    const [taskDialogDisabled, setTaskDialogDisabled] = useState(false);

    const classes = useStyles();

    useEffect(() => {
        setTimeline(null);

        setIsInitializing(true);
        loadTimeline();
    },[companyId]);

    const isEditable = useMemo(() => {
        return userInfo && userInfo.roles && userInfo.roles.some(r => ['ADMIN-SYS-ADMIN','ADMIN-SYS-MANAGER'].includes(r));
    }, [userInfo])

    const loadTimeline = (finallyAction) => {
        setIsLoading(true);

        apiGet(apiUrls.getBrdTimeline(companyId))
            .then((response) => {
                if (response.data) {
                    setTimeline(response.data);
                }
            })
            .finally(() => {
                setIsLoading(false);
                setIsInitializing(false);

                if (finallyAction) {
                    finallyAction();
                }
            });
    }

    const createTimelineTasks = (timeline) => {
        var newTasks = [];

        for (var i = 0; i < timeline.brdTimelineMilestones.length; i++) {
            var milestone = timeline.brdTimelineMilestones[i];

            newTasks.push(
                {
                    name: milestone.title,
                    custom_class: `milestone ${classes.milestone}`,
                    start: new Date (Math.min.apply(null, milestone.brdTimelineSteps.map(s => new Date(s.targetDate ?? s.dueDate)))),
                    end: new Date(Math.max.apply(null, milestone.brdTimelineSteps.map(s => new Date(s.dueDate)))),
                }
            );

            for (var j = 0; j < milestone.brdTimelineSteps.length; j++) {
                var step = milestone.brdTimelineSteps[j];

                newTasks.push(
                    {
                        id: step.brdTimelineStepId,
                        name: step.title,
                        description: step.stepDescription,
                        responsibility: step.responsibility,
                        start: step.targetDate ?? step.dueDate,
                        end: step.dueDate,
                        progress: step.completionPercentage,
                        dependencies: step.dependencies ? step.dependencies.join(",") : null,
                        custom_class: step.completionPercentage === 100 ? classes.complete :
                            moment(step.dueDate) < moment() ? classes.overdue :
                            moment(step.dueDate).add(-2, "days") < moment() ? classes.atRisk : classes.onTime
                    }
                );
            }
        }

        return newTasks;
    }

    useEffect(() => {
        var t = [];

        if (timeline && timeline.brdTimelineMilestones) {
            t = createTimelineTasks(timeline);
        }

        setTasks(t);
        setUndoStack([]);
        setRedoStack([]);
    }, [timeline]);

    useEffect(() => {
        setCurrentTasks(tasks.map(a => { return {...a}}));
    },[tasks])

    const handleTasksChange = (updatedTasks) => {
        setUpdatedTasks(updatedTasks);
    }

    useEffect(() => {
        if (JSON.stringify(currentTasks) != JSON.stringify(updatedTasks)) {
            setUndoStack(current => {
                current.push([...currentTasks].map(a => { return {...a}}))

                return current;
            });
            setRedoStack([]);
            setTasks(updatedTasks);
        }
    },[updatedTasks]);

    const handleTaskClick = (task) => {
        if (isEditing) {
            setDoShowTaskDialog(true);
            setEditTask(task);
        }
    }

    const completeEdit = (doSave, task) => {
        if (doSave) {
            var allTasks = [...tasks];

            var t = allTasks.find(a => a.id === task.id);

            if (t &&
                (t.name !== task.name ||
                 t.start !== task.start ||
                 t.end !== task.end ||
                 t.progress !== task.progress)) {
                t.name = task.name;
                t.start = task.start;
                t.end = task.end;
                t.progress = task.progress;
                
                setUndoStack(current => {
                    current.push(currentTasks.map(a => { return {...a}}))

                    return current;
                });

                setRedoStack([]);
                setTasks(allTasks);
            }

            setDoShowTaskDialog(false);
        }
        else {
            setDoShowTaskDialog(false);
        }
    }

    const undoChange = () => {
        if (undoStack.length > 0) {
            var copyUndo = [...undoStack];
            var undoTasks = copyUndo.pop();
            setUndoStack(copyUndo);

            var copyRedo = [...redoStack];
            copyRedo.push(tasks);
            setRedoStack(copyRedo);

            setTasks(undoTasks);
        }
    }

    const redoChange = () => {
        if (redoStack.length > 0) {
            var copyRedo = [...redoStack];
            var redoTasks = copyRedo.pop();
            setRedoStack(copyRedo);

            var copyUndo = [...undoStack]
            copyUndo.push(tasks);
            setUndoStack(copyUndo)

            setTasks(redoTasks)
        }
    }

    const promptReset = () => {
        setConfirmCloseAction(() => (result) => reset(result));
        setConfirmTitle('Reset Timeline');
        setConfirmText(`Are you sure you would like to undo all changes the BRD timeline?`);
        setDoShowConfirm(true);
    }

    const reset = (isConfirmed) => {
        if (isConfirmed) {
            setConfirmButtonsDiabled(true);
            loadTimeline(() => hideConfirm(false));
        }
        else {
          hideConfirm();
        }
    }

    const promptSave = () => {
        setConfirmCloseAction(() => (result) => save(result));
        setConfirmTitle('Save Timeline');
        setConfirmText(`Are you sure you would like to save the BRD timeline?`);
        setDoShowConfirm(true);
    }

    const save = (isConfirmed) => {
        if (isConfirmed) {
            var steps = tasks.filter(t => Utils.isGuid(t.id))
                .map(t => {
                return {
                    brdTimelineStepId: t.id,
                    targetDate: t.start,
                    dueDate: t.end,
                    completionPercentage: t.progress,
                    title: t.name,
                    responsibility: t.responsibility,
                    stepDescription: t.description,
                }});
            
            setConfirmButtonsDiabled(true);
            apiPost(apiUrls.postBrdTimeline(companyId), steps)
                .then((response) => {
                    loadTimeline(() => hideConfirm());
                })
                .catch((response) => {

                })
                .finally(() => {

                });
        }
        else {
            hideConfirm();
        }
    }

    const promptCreate = () => {
        setConfirmCloseAction(() => (result) => create(result));
        setConfirmTitle('Create Timeline');
        setConfirmText(`Are you sure you would like to create the BRD timeline?`);
        setDoShowConfirm(true);
    }

    const create = (isConfirmed) => {
        if (isConfirmed) {
            setConfirmButtonsDiabled(true);
            apiPost(apiUrls.createBrdTimeline(companyId), { timelineType, clientEffectiveDate, timelineStartDate })
                .then((response) => {
                    loadTimeline(() => hideConfirm())
                })
                .catch((response) => {

                })
                .finally(() => {
                    
                });
        }
        else
        {
            hideConfirm();
        }
    }

    const promptRemove = () => {
        setConfirmCloseAction(() => (result) => remove(result));
        setConfirmTitle('Delete Timeline');
        setConfirmText(`Are you sure you would like to delete the BRD timeline?`);
        setDoShowConfirm(true);
    }

    const remove = (isConfirmed) => {
        if (isConfirmed) {
            setConfirmButtonsDiabled(true);
            apiPost(apiUrls.deleteBrdTimeline(companyId))
                .then((response) => {
                    setTimeline(null);
        
                    setIsInitializing(true);
                    loadTimeline(() => hideConfirm(false));
                })
                .catch((response) => {

                })
                .finally(() => {
                    
                });
        }
        else {
            hideConfirm();
        }
    }

    const hideConfirm = () => {
        setDoShowConfirm(false);
        setConfirmButtonsDiabled(false);
    }

    const handleConfirmClose = (result) => {
        confirmCloseAction(result);
    }

    return (
        <>
            {isInitializing ? <LoaderAnimation /> :
            timeline ?
                <Box>
                    <Box component="h4">
                        Client Effective Date: {Utils.formatDate(timeline?.clientEffectiveDate)}
                    </Box>
                     <Box className="d-flex" style={{ justifyContent: "space-between" }}>
                        <Box>
                            {isEditable ?
                                <FormGroup>
                                    <FormControlLabel control={
                                        <Switch
                                            checked={isEditing}
                                            onChange={() => setIsEditing(!isEditing)}
                                            color="success"
                                        />
                                        } 
                                        label="Enable Editing"
                                    />
                                </FormGroup> : null}
                        </Box>
                        <Box>
                            {!isEditing ?
                                <Box className="d-flex">
                                    <ContactAXIM />
                                    <BrdDocumentLink className="ms-2 mb-2"/>
                                </Box>
                            :
                            <Box className="mb-2">
                                <Button
                                    className="ms-2"
                                    color="primary"
                                    variant="outlined"
                                    onClick={undoChange}
                                    disabled={undoStack.length === 0}
                                    startIcon={<UndoIcon />}
                                >
                                    Undo
                                </Button>
                                <Button
                                    className="ms-2"
                                    color="primary"
                                    variant="outlined"
                                    onClick={redoChange}
                                    disabled={redoStack.length === 0}
                                    startIcon={<RedoIcon />}
                                >
                                    Redo
                                </Button>
                                <LoadingButton
                                    className="ms-2"
                                    color="primary"
                                    variant="outlined"
                                    onClick={promptReset}
                                    loading={isLoading}
                                    loadingPosition="start"
                                    disabled={undoStack.length === 0}
                                    startIcon={<ReplayOutlinedIcon />}
                                >
                                    Undo All Changes
                                </LoadingButton>
                                <LoadingButton
                                    className="ms-2"
                                    color="primary"
                                    variant="outlined"
                                    onClick={promptSave}
                                    loading={isLoading}
                                    loadingPosition="start"
                                    startIcon={<SaveOutlinedIcon />}
                                    disabled={undoStack.length === 0}
                                >
                                    Save
                                </LoadingButton>
                                <LoadingButton
                                    className="ms-2"
                                    color="primary"
                                    variant="outlined"
                                    onClick={promptRemove}
                                    loading={isLoading}
                                    loadingPosition="start"
                                    startIcon={<DeleteOutlineOutlinedIcon />}
                                >
                                    Delete
                                </LoadingButton>
                                <BrdDocumentLink className="ms-2" />
                            </Box>}
                        </Box>
                    </Box>

                    {undoStack.length !== 0 ? 
                        <Box className="d-flex" style={{ justifyContent: "center", textAlign: "center"}}>
                            <Typography variant="h6" className="mb-2" style={{ background: "red", width: "500px", borderRadius: "10px" }}>There are unsaved changes.</Typography>
                        </Box>
                    : null}

                    {tasks && tasks.length > 0 ?
                        <AximGantt
                            tasks={tasks}
                            readonly={!isEditing}
                            onTasksChange={handleTasksChange}
                            onClickTask={handleTaskClick}
                            customClass={classes.gantt}
                        /> : null}
                </Box>
                : isEditable ?
                <Box className="d-flex" style={{ flexDirection: "column", textAlign: "center" }}>
                    <Box component="h4" className="mb-2">
                        Generate a new BRD Timeline
                    </Box>
                    <AximDropdown
                        label="Select Timeline Type"
                        handleChange={e => setTimelineType(e.target.value)}
                        value={timelineType}
                        pairs={[{ value: "compliance-only", text: "Compliance Only" },
                                { value: "compliance-benefits", text: "Compliance and Benefits Administration" },
                                { value: "prevailing-wage", text: "Prevailing Wage Timeline" }]}
                    />
                    <Box className="mb-2">
                        <TextField
                            variant="standard"
                            label="Client Effective Date"
                            type="date"
                            value={clientEffectiveDate}
                            onChange={e => setClientEffectiveDate(e.target.value)}
                        />
                    </Box>
                    <Box className="mb-2">
                        <TextField
                            variant="standard"
                            label="Timeline Start Date"
                            type="date"
                            value={timelineStartDate}
                            onChange={e => setTimelineStartDate(e.target.value)}
                        />
                    </Box>
                    <Box className="mb-2">
                        <LoadingButton
                            className="ms-2"
                            color="primary"
                            variant="outlined"
                            onClick={promptCreate}
                            loading={isLoading}
                            loadingPosition="start"
                            startIcon={<SaveOutlinedIcon />}
                        >
                            Create
                        </LoadingButton>
                    </Box>
                </Box>
                :
                <Box sx={{ textAlign: "center" }}>
                    <Box component="h4">
                        Implementation Timeline has not been created yet.
                    </Box>
                    
                    <Box className="d-flex" style={{ justifyContent: "center" }}>
                        <ContactAXIM />
                    </Box>
                </Box>
            }
            
            <ConfirmDialog
                doShowConfirm={doShowConfirm}
                handleClose={handleConfirmClose}
                confirmTitle={confirmTitle}
                confirmText={confirmText}
                buttonsDisabled={confirmButtonsDisabled} />

            <EditDialog
                doShow={doShowTaskDialog}
                buttonsDisabled={taskDialogDisabled}
                task={editTask}
                completeEdit={completeEdit}
            />
        </>
      );
}